// View registry + hash router skeleton.
//
// Blueprint IA: three tiers.
//  - CARE    — daily clinical work; always visible.
//  - OPERATE — periodic/back-office surfaces; hidden in 'simple' mode
//              (legacy simple mode hid exactly import/billing/reports).
//  - MANAGE  — admin; visible only to admin-tier roles.
// Routes use location.hash ('#/board'); unknown or inaccessible routes
// resolve to the default view.
//
// A route id is the FIRST path segment of the hash, so a view may own a
// sub-path: '#/reports/access' is the 'reports' route showing its Access area.
// Only Reports uses this today (its area pivot); every other route is a bare id.

import { REPORT_AREA_KEYS } from '../metrics/registry.js';

export const TIERS = Object.freeze({ CARE: 'CARE', OPERATE: 'OPERATE', MANAGE: 'MANAGE' });

export const DEFAULT_VIEW = 'today';

// Route table (Blueprint route ids; legacy names in comments).
export const ROUTES = Object.freeze([
  Object.freeze({ id: 'today', title: 'Today', tier: TIERS.CARE }), // legacy 'today'
  Object.freeze({ id: 'consents', title: 'New consents', tier: TIERS.CARE }), // legacy 'intake'
  Object.freeze({ id: 'board', title: 'Patient board', tier: TIERS.CARE }), // legacy 'pipeline'
  Object.freeze({ id: 'patients', title: 'Patients', tier: TIERS.CARE }),
  Object.freeze({ id: 'legacy', title: 'Legacy patients', tier: TIERS.CARE }), // 2024-26 register migration
  Object.freeze({ id: 'safety', title: 'Adverse events', tier: TIERS.CARE }),
  Object.freeze({ id: 'reports', title: 'Reports', tier: TIERS.OPERATE }),
  Object.freeze({ id: 'billing', title: 'Billing', tier: TIERS.OPERATE }),
  Object.freeze({ id: 'import', title: 'Bulk import', tier: TIERS.OPERATE }),
  // Training sits in OPERATE because it is not clinical work — but it is
  // exempt from the Simple-view hide. Simple view is what a ward nurse uses,
  // and she is exactly who the course is for: hiding it there would put the
  // training behind the one setting its audience is most likely to be on.
  Object.freeze({ id: 'training', title: 'Training', tier: TIERS.OPERATE, alwaysVisible: true }),
  // Team activity carries the two panels that were pulled out of Reports on
  // 2026-08-14. Reports is shared, so a nurse could see that admins were being
  // shown her sign-in times and her scores; MANAGE is where that belongs.
  Object.freeze({ id: 'team', title: 'Team activity', tier: TIERS.MANAGE }),
  Object.freeze({ id: 'admin', title: 'Admin', tier: TIERS.MANAGE }),
]);

/** @type {Map<string, {id: string, render: (host: Element) => void, load?: () => any}>} */
const views = new Map();

/**
 * Register a view implementation for a route id.
 * @param {string} id route id (must exist in ROUTES)
 * @param {{render: (host: Element) => void, load?: () => any}} def
 * @returns {void}
 */
export function registerView(id, def) {
  if (!ROUTES.some((r) => r.id === id)) throw new Error('unknown route id: ' + id);
  views.set(id, Object.assign({ id }, def));
}

/**
 * @param {string} id
 * @returns {{id: string, render: (host: Element) => void, load?: () => void}|undefined}
 */
export function getView(id) {
  return views.get(id);
}

/** Test-only: clear registered views. @returns {void} */
export function _resetViews() {
  views.clear();
}

// ─── Refresh hook (F-NAV-03) ───────────────────────────────────────────────
//
// The Sync control needs ONE way to say "re-run whatever loads this screen"
// without knowing anything about the screen. Every data view already exposes
// load(); the only change asked of them was to RETURN the promise so a caller
// can await it — a loader that resolves before its data lands would give the
// Sync button a busy state that lies.
//
// A view with no load() (Bulk import: its state is an unsaved local preview,
// and silently re-running it would throw away work the user is mid-way
// through) is not an error. It reports 'noop' so the caller can say so.

/**
 * Pure: find the first failed {ok:false} envelope in whatever a loader
 * returned — one envelope, an array of them (Promise.all), or nothing at all.
 *
 * This exists because the data loaders DELIBERATELY do not throw: a failed
 * load lands as `loadError` on its slice so the view can render "couldn't load
 * — tap to retry". That is right for the view and a trap for Sync: an awaited
 * loader that handled its own error resolves normally, so a naive Sync would
 * announce "Refreshed" over a screen that just failed to refresh. Exactly the
 * false all-clear the house rules forbid. So we read the envelope instead of
 * trusting the absence of a throw.
 * @param {any} res whatever load() returned
 * @returns {{ok: boolean, error?: any, code?: any, reported?: boolean}|null} the first failure, or null
 */
export function firstFailure(res) {
  if (!res || typeof res !== 'object') return null;
  if (Array.isArray(res)) {
    for (const r of res) {
      const f = firstFailure(r);
      if (f) return f;
    }
    return null;
  }
  return res.ok === false ? res : null;
}

/**
 * Re-run a view's loader. Never throws — returns the house envelope so the
 * caller decides what the user sees. `data: 'noop'` means the view has no
 * loader; `data: id` means its loader ran AND reported no failure.
 * @param {string} id route id
 * @returns {Promise<{ok: boolean, data?: string, error?: any, code?: any, reported?: boolean}>}
 */
export async function reloadView(id) {
  const v = views.get(id);
  if (!v) return { ok: false, error: new Error('no view registered: ' + id), code: 'NO_VIEW' };
  if (typeof v.load !== 'function') return { ok: true, data: 'noop' };
  try {
    const res = await v.load();
    const failed = firstFailure(res);
    if (failed) {
      // `reported: true` means the view ALREADY told the user (Reports and
      // Billing toast their own load failures, because those failures matter
      // on a plain navigation too, not only on a Sync). Passing the flag on
      // lets the caller stay quiet instead of stacking a second toast about
      // the same event — while still never reporting success.
      return {
        ok: false,
        error: failed.error,
        code: failed.code || 'LOAD_FAILED',
        reported: !!failed.reported,
      };
    }
    return { ok: true, data: id };
  } catch (e) {
    // A loader that rejects outright has already failed to null its stale data
    // (house rule: error paths null explicitly). Surface it rather than
    // swallow — the caller toasts through netErr and the screen keeps whatever
    // it had, which is the honest outcome for a refresh that did not happen.
    return { ok: false, error: e, code: 'RELOAD_FAILED' };
  }
}

// ─── Shared list-state presentation ────────────────────────────────────────

/**
 * Pure: the text a count chip shows for a list state. ONLY 'ready' has earned
 * a number.
 *
 * A "0" beside Serious adverse events, or on the five Today tiles, or on the
 * New-consents tabs, is a clinical claim. The views originally dashed it while
 * a load was in flight and printed a hard 0 once the load RESOLVED — including
 * when it resolved as a failure, so "Serious 0" rendered directly above
 * "Couldn't load the safety register". The error card beside it makes this a
 * weaker signal than the in-flight case, but it is the same wrong claim, and
 * the rule belongs in one place rather than three.
 * @param {'loading'|'error'|'ready'|string} state
 * @param {number|string} value
 * @returns {string}
 */
export function countText(state, value) {
  return state === 'ready' ? String(value) : '—';
}

// ─── Pure routing logic ────────────────────────────────────────────────────

/**
 * The raw first path segment of a hash (before any '/sub' or '?query'), lowered
 * of its '#'/'/' prefixes and trimmed. NOT validated against ROUTES — this is
 * the mechanical parse that resolveRoute() and syncHash() both build on.
 * @param {string|null|undefined} hash
 * @returns {string}
 */
function baseSegment(hash) {
  return String(hash || '')
    .replace(/^#/, '')
    .replace(/^\//, '')
    .split('?')[0]
    .split('/')[0]
    .trim();
}

/**
 * Resolve a location.hash to a route id. Accepts '#/board', '#board',
 * '/board', 'board'; '' / '#' / '#/' and unknown ids resolve to DEFAULT_VIEW.
 * The route id is the FIRST path segment, so '#/reports/access' resolves to
 * 'reports' (its Access area) rather than falling through to the default.
 * @param {string|null|undefined} hash
 * @returns {string}
 */
export function resolveRoute(hash) {
  const id = baseSegment(hash);
  return ROUTES.some((r) => r.id === id) ? id : DEFAULT_VIEW;
}

/**
 * The Reports area named by a hash: the 2nd path segment of '#/reports/<area>',
 * validated against the pivot's areas. Anything that is not a reports hash with
 * a known area — '#/reports', '#/reports/bogus', '#/board', '' — is 'overview',
 * the safe default landing. Case-insensitive. Pure; no window access.
 * @param {string|null|undefined} hash
 * @returns {string}
 */
export function reportArea(hash) {
  const path = String(hash || '')
    .replace(/^#/, '')
    .replace(/^\//, '')
    .split('?')[0]
    .trim();
  const segs = path.split('/');
  if (segs[0] !== 'reports') return 'overview';
  const area = String(segs[1] || '').toLowerCase();
  return REPORT_AREA_KEYS.includes(area) ? area : 'overview';
}

/**
 * Tier-gating: which routes are visible for a UI mode + role.
 *  - mode 'simple' hides the OPERATE tier (legacy simple mode), EXCEPT routes
 *    marked alwaysVisible — a route may be grouped under Operate for where it
 *    belongs in the menu without being gated away from the staff who need it;
 *  - MANAGE requires isAdmin regardless of mode.
 * @param {{mode?: 'simple'|'full', isAdmin?: boolean}} [ctx]
 * @returns {Array<{id: string, title: string, tier: string, alwaysVisible?: boolean}>}
 */
export function visibleRoutes(ctx) {
  const mode = (ctx && ctx.mode) || 'full';
  const isAdmin = !!(ctx && ctx.isAdmin);
  return ROUTES.filter((r) => {
    const always = !!(/** @type {any} */ (r).alwaysVisible);
    if (r.tier === TIERS.OPERATE && mode === 'simple' && !always) return false;
    if (r.tier === TIERS.MANAGE && !isAdmin) return false;
    return true;
  });
}

/**
 * May this route be entered in this context? (Same rules as visibility.)
 * @param {string} id
 * @param {{mode?: 'simple'|'full', isAdmin?: boolean}} [ctx]
 * @returns {boolean}
 */
export function canAccess(id, ctx) {
  return visibleRoutes(ctx).some((r) => r.id === id);
}

/**
 * Full navigation decision: requested hash -> the route to actually show,
 * falling back to DEFAULT_VIEW when the resolved route is tier-gated off
 * (mirrors legacy toggleUIMode() bouncing import/billing/reports -> today).
 * @param {string|null|undefined} hash
 * @param {{mode?: 'simple'|'full', isAdmin?: boolean}} [ctx]
 * @returns {string}
 */
export function routeFor(hash, ctx) {
  const id = resolveRoute(hash);
  return canAccess(id, ctx) ? id : DEFAULT_VIEW;
}

// ─── Hash router (thin DOM wiring) ─────────────────────────────────────────

/**
 * Start the router: fires onRoute for the current hash now and on every
 * hashchange. Returns a stop function.
 * @param {(id: string) => void} onRoute
 * @param {any} [win] defaults to globalThis (injectable for tests)
 * @returns {() => void}
 */
export function startRouter(onRoute, win) {
  const w = win || /** @type {any} */ (globalThis);
  const fire = () => onRoute(resolveRoute(w.location && w.location.hash));
  w.addEventListener('hashchange', fire);
  fire();
  return () => w.removeEventListener('hashchange', fire);
}

/**
 * Navigate to a route by id (sets the hash; the router listener renders).
 * @param {string} id
 * @param {any} [win]
 * @returns {void}
 */
export function navigate(id, win) {
  const w = win || /** @type {any} */ (globalThis);
  w.location.hash = '#/' + id;
}

/**
 * Make the address bar agree with the route actually rendered. Needed when
 * routeFor() bounced a gated hash (e.g. '#/reports' in simple mode renders
 * 'today'): without this the stale hash lingers, and a later navigate() to the
 * formerly-gated route assigns an IDENTICAL hash — no hashchange fires and the
 * nav button is silently dead. replaceState avoids a history entry and does
 * not re-fire hashchange.
 * @param {string} id the route id that was actually rendered
 * @param {any} [win]
 * @returns {void}
 */
export function syncHash(id, win) {
  const w = win || /** @type {any} */ (globalThis);
  if (!w.location) return;
  const cur = String(w.location.hash || '');
  // If the base route is ALREADY correct, leave the hash untouched so any
  // sub-path survives: '#/reports/access' must not be rewritten to '#/reports'
  // (which would drop the area and bounce the pivot back to Overview on every
  // route pass). We only rewrite when the gate actually bounced us elsewhere.
  if (baseSegment(cur) === id) return;
  const target = '#/' + id;
  if (cur === target) return;
  if (w.history && typeof w.history.replaceState === 'function') {
    w.history.replaceState(null, '', target);
  }
}
